export type CheckInCadence = 'weekly' | 'biweekly' | 'monthly';

export interface CheckInPeriod {
	start: string;
	end: string;
	next: string;
}

/**
 * Get display text for toast messages (different from banner text)
 */
export const getCadenceToastText = (cadence?: string): string => {
	switch (cadence) {
		case 'weekly':
			return 'Weekly pulse checks';
		case 'biweekly':
			return 'Bi-weekly pulse checks';
		case 'monthly':
			return 'Monthly pulse checks';
		default:
			return 'Pulse checks';
	}
};

/**
 * Get display text for a check-in cadence
 */
export const getCadenceDisplayText = (cadence?: string): string => {
	switch (cadence) {
		case 'weekly':
			return 'week';
		case 'biweekly':
			return 'other week';
		case 'monthly':
			return 'month';
		default:
			return 'not set up';
	}
};

/**
 * Get the check-in day for display
 */
export const getCheckInDay = (cadence?: string): string => {
	if (!cadence) return '';

	switch (cadence) {
		case 'weekly':
			return 'Wednesday';
		case 'biweekly':
			return 'the second Monday';
		case 'monthly': {
			return `the 3rd Monday of the month`;
		}
		default:
			return '';
	}
};

/**
 * Calculate weeks since beginning of year starting from first Monday (matches backend logic)
 * Backend equivalent: CheckIns::DateUtils.weeks_since_year_start
 * Ruby logic: ((current_monday.to_date - year_start_monday.to_date) / 7).to_i
 */
export const getWeeksSinceYearStart = (date: Date): number => {
	const yearStart = new Date(date.getFullYear(), 0, 1);
	let yearStartMonday = new Date(yearStart);

	const dayOfWeek = yearStart.getDay(); // 0 = Sunday, 1 = Monday, etc.
	const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert to Monday-based week
	yearStartMonday.setDate(yearStart.getDate() - daysToSubtract);

	const currentMonday = new Date(date);
	const currentDayOfWeek = date.getDay();
	const currentDaysToSubtract = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
	currentMonday.setDate(date.getDate() - currentDaysToSubtract);

	const timeDiffMs = currentMonday.getTime() - yearStartMonday.getTime();
	const daysDiff = Math.floor(timeDiffMs / (24 * 60 * 60 * 1000));
	return Math.floor(daysDiff / 7);
};

/**
 * Get the start Monday of the current biweekly period (matches backend logic)
 */
export const getBiweeklyPeriodStart = (date: Date = new Date()): Date => {
	const currentWeek = getWeeksSinceYearStart(date);

	// Get Monday of current week
	const mondayOfCurrentWeek = new Date(date);
	const daysSinceMonday = (date.getDay() + 6) % 7;
	mondayOfCurrentWeek.setDate(date.getDate() - daysSinceMonday);

	if (currentWeek % 2 === 1) {
		// Odd weeks: period started previous week Monday
		const periodStart = new Date(mondayOfCurrentWeek);
		periodStart.setDate(mondayOfCurrentWeek.getDate() - 7);
		return periodStart;
	} else {
		// Even weeks: period starts this week Monday
		return mondayOfCurrentWeek;
	}
};

/**
 * Get the next biweekly Monday (current period start + 2 weeks)
 */
export const getNextBiweeklyMonday = (): Date => {
	const currentPeriodStart = getBiweeklyPeriodStart();
	const nextPeriodStart = new Date(currentPeriodStart);
	nextPeriodStart.setDate(currentPeriodStart.getDate() + 14); // Add 2 weeks
	return nextPeriodStart;
};

/**
 * Calculate the current check-in period dates based on cadence
 *
 * @param cadence - The check-in frequency
 * @param scheduledAt - When the next check-in period is scheduled to start (optional)
 * @returns Object with formatted start, end, and next check-in dates
 */
export const getCheckInPeriod = (cadence?: string, scheduledAt?: string | Date): CheckInPeriod => {
	if (!cadence) return { start: '', end: '', next: '' };

	const now = new Date();
	const scheduledDate = scheduledAt ? new Date(scheduledAt) : null;
	let startDate: Date;
	let endDate: Date;
	const useUpcomingPeriod = scheduledDate && scheduledDate > now;

	switch (cadence) {
		case 'weekly': {
			if (useUpcomingPeriod) {
				// Next week: Monday to Friday
				startDate = new Date(now);
				const daysUntilMonday = (1 - now.getDay() + 7) % 7;
				startDate.setDate(now.getDate() + (daysUntilMonday === 0 ? 7 : daysUntilMonday));
			} else {
				// Current week: Monday to Friday
				startDate = new Date(now);
				const daysSinceMonday = (now.getDay() + 6) % 7;
				startDate.setDate(now.getDate() - daysSinceMonday);
			}

			// End date is Sunday of the same week
			endDate = new Date(startDate);
			endDate.setDate(startDate.getDate() + 6);
			break;
		}
		case 'biweekly': {
			if (useUpcomingPeriod) {
				// Next biweekly period
				startDate = getNextBiweeklyMonday();
				// End date is Sunday 2 weeks later
				endDate = new Date(startDate);
				endDate.setDate(startDate.getDate() + 13); // 2 weeks - 1 day = 13 days to Sunday
			} else {
				// Current biweekly period: Use the clean helper function
				startDate = getBiweeklyPeriodStart(now);

				// End date is Sunday 2 weeks after start
				endDate = new Date(startDate);
				endDate.setDate(startDate.getDate() + 13); // 2 weeks - 1 day = Sunday
			}
			break;
		}
		case 'monthly': {
			if (useUpcomingPeriod) {
				// Next month: 1st to last day
				startDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
				endDate = new Date(now.getFullYear(), now.getMonth() + 2, 0);
			} else {
				// Current month: 1st to last day
				startDate = new Date(now.getFullYear(), now.getMonth(), 1);
				endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
			}
			break;
		}
		default:
			return { start: '', end: '', next: '' };
	}

	const formatDateWithWeekday = (date: Date) =>
		date.toLocaleDateString('en-US', {
			weekday: 'long',
			month: 'short',
			day: 'numeric',
		});

	const formatDateShort = (date: Date) =>
		date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
		});

	// Use different formatting for monthly vs weekly/biweekly
	const formatStartEnd = cadence === 'monthly' ? formatDateShort : formatDateWithWeekday;

	return {
		start: formatStartEnd(startDate),
		end: formatStartEnd(endDate),
		next: getCheckInDay(cadence),
	};
};

/**
 * Get the start of next month for monthly check-ins
 */
export const getMonthlyStartDate = (): string => {
	const now = new Date();
	const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
	return nextMonth.toLocaleDateString('en-US', {
		month: 'long',
		day: 'numeric',
	});
};

/**
 * Get the next start date for check-ins as a Date object (for backend)
 */
export const getNextStartDateAsDate = (cadence?: string): Date | null => {
	if (!cadence) return null;

	const now = new Date();
	let startDate: Date;

	switch (cadence) {
		case 'weekly': {
			// Next Monday
			startDate = new Date(now);
			const daysUntilMonday = (1 - now.getDay() + 7) % 7;
			startDate.setDate(now.getDate() + (daysUntilMonday === 0 ? 7 : daysUntilMonday));
			break;
		}
		case 'biweekly': {
			// Next biweekly Monday (using backend odd week logic)
			startDate = getNextBiweeklyMonday();
			break;
		}
		case 'monthly': {
			// 1st of next month
			startDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
			break;
		}
		default:
			return null;
	}

	return startDate;
};

/**
 * Get the next start date for check-ins (when they will begin) - formatted for display
 */
export const getNextStartDate = (cadence?: string): string => {
	const startDate = getNextStartDateAsDate(cadence);
	if (!startDate) return '';

	if (cadence === 'monthly') {
		return startDate.toLocaleDateString('en-US', {
			month: 'long',
			day: 'numeric',
		});
	}

	return startDate.toLocaleDateString('en-US', {
		weekday: 'long',
		month: 'short',
		day: 'numeric',
	});
};
